/** Vakansiya sahifasi (eskiz 15): talablar, nomzodlar, driver topshirish oynasi. */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { ApiError } from "@/lib/api";
import { formatDate, formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { can, type Me } from "@/features/auth/types";
import { useCarrier, useJob, useJobCandidates } from "@/features/carriers/api";
import { experienceLabel, statesLabel, type Carrier, type Job, type JobCandidates } from "@/features/carriers/types";
import type { ApplicationStatus } from "@/features/drivers/types";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { StatusPill } from "@/components/ui/badge";
import { ErrorState, Kv, PageLoading, Spinner } from "@/components/ui/misc";
import { ApplicationPill, CarrierReply } from "@/pages/carriers/shared";
import { JobFormModal } from "./JobFormModal";
import { SubmitDriverModal } from "./SubmitDriverModal";
import { ContextMenu } from "@/components/ui/context-menu";
import { useDriverActions } from "@/pages/drivers/useDriverActions";
import { PersonLink } from "@/components/PersonLink";

const SUMMARY_ORDER: ApplicationStatus[] = ["submitted", "background_check", "orientation", "hired", "rejected", "withdrawn"];

export function JobDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation("jobs");
  const me = useMe();
  const job = useJob(id);
  if (me.isPending || job.isPending) return <PageLoading />;
  if (!me.data) return null;
  if (job.isError) return <ErrorState message={job.error instanceof ApiError && job.error.status === 404 ? t("detail.notFound") : job.error.message} onRetry={() => job.refetch()} />;
  return <Detail me={me.data} job={job.data} />;
}

function Detail({ me, job }: { me: Me; job: Job }) {
  const { t } = useTranslation("jobs");
  const manager = can(me, "carriers_edit");
  const carrier = useCarrier(job.carrier);
  const candidates = useJobCandidates(job.id);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const reqs = job.requirements ?? {};

  return (
    <div>
      <div className="mb-3 text-[12px] text-muted">
        <Link to="/jobs" className="hover:underline">
          {t("title")}
        </Link>{" "}
        / <span className="font-semibold text-ink">{job.title}</span>
      </div>

      <Card className="mb-4 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[22px] leading-tight font-bold">{job.title}</h1>
              <StatusPill status={job.status} />
            </div>
            <div className="mt-1 text-[13px] text-ink-soft">
              <Link to={`/carriers/${job.carrier}`} className="font-semibold text-ink hover:underline">
                {job.carrier_name}
              </Link>
              {carrier.data?.mc_number && ` · ${carrier.data.mc_number}`}
              {job.posted_at && ` · ${t("detail.posted", { date: formatDate(job.posted_at) })}`}
              {job.carrier_status !== "active" && <span className="ml-2 rounded bg-status-rejected px-1.5 py-0.5 text-[11px] font-semibold">{t("detail.carrierStatus", { status: t(`common:status.${job.carrier_status}`).toLowerCase() })}</span>}
            </div>
            <div className="mt-1 text-[13px] text-ink-soft">
              {t("detail.openings")} <span className="font-semibold text-ink">{t("hiredOf", { hired: job.hired_count, openings: job.openings })}</span> · {t("detail.openingsTail", { count: job.candidates_count })}
              {job.carrier_trucks_available > 0 && (
                <>
                  {" · "}
                  <Link to={`/carriers/${job.carrier}`} className="font-semibold text-success hover:underline">
                    {t("carriers:trucks.freeCount", { count: job.carrier_trucks_available })}
                  </Link>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            {manager && <Button onClick={() => setEditOpen(true)}>{t("common:actions.edit")}</Button>}
            <Button variant="primary" onClick={() => setSubmitOpen(true)} disabled={job.status !== "open"} title={job.status !== "open" ? t("detail.notOpen") : undefined}>
              {t("detail.submitDriver")}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.15fr]">
        <div className="space-y-4">
          <Card>
            <CardHeader title={t("detail.requirements")} />
            <CardBody>
              <Kv label={t("detail.cdl")}>{t("detail.cdlClass", { cls: job.cdl_class || "A" })}</Kv>
              <Kv label={t("detail.experience")}>{experienceLabel(job.min_experience_months)} {job.driving_type ? job.driving_type : ""}</Kv>
              <Kv label={t("detail.hiringStates")}>{statesLabel(job.states)}</Kv>
              <Kv label={t("detail.mvr")} tone={reqs.mvr ? undefined : "muted"}>{reqs.mvr || t("detail.notSpecified")}</Kv>
              <Kv label={t("detail.dui")} tone={reqs.dui ? undefined : "muted"}>{reqs.dui || t("detail.notSpecified")}</Kv>
              <Kv label={t("detail.drugTest")} tone={reqs.drug_test ? undefined : "muted"}>{reqs.drug_test || t("detail.notSpecified")}</Kv>
              <Kv label={t("detail.endorsements")}>{job.endorsements_required.length ? job.endorsements_required.join(", ") : t("detail.notRequired")}</Kv>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t("detail.paySchedule")} />
            <CardBody>
              <Kv label={t("detail.pay")}>{job.pay_text || "—"}</Kv>
              <Kv label={t("detail.homeTime")}>{job.home_time || "—"}</Kv>
              <Kv label={t("detail.equipment")}>{job.equipment || "—"}</Kv>
              <Kv label={t("detail.signOnBonus")}>{job.sign_on_bonus || "—"}</Kv>
              {job.notes && <Kv label={t("detail.notes")}>{job.notes}</Kv>}
            </CardBody>
          </Card>
          <div className="rounded-card border border-dashed border-border bg-fill/40 px-5 py-4 text-muted">
            <div className="text-[14px] font-semibold">{t("detail.matchingTitle")}</div>
            <p className="mt-1 text-[12px]">{t("detail.matchingText")}</p>
          </div>
        </div>

        <div className="space-y-4">
          <CandidatesCard job={job} data={candidates.data} loading={candidates.isPending} me={me} />
          <Card>
            <CardHeader title={t("detail.contactTitle")} />
            <CardBody>
              {carrier.isPending ? (
                <Spinner />
              ) : carrier.data ? (
                <ContactRows carrier={carrier.data} />
              ) : (
                <p className="text-[13px] text-muted">{t("detail.contactUnavailable")}</p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <SubmitDriverModal open={submitOpen} onOpenChange={setSubmitOpen} job={job} />
      <JobFormModal open={editOpen} onOpenChange={setEditOpen} job={job} />
    </div>
  );
}

function ContactRows({ carrier }: { carrier: Carrier }) {
  const { t } = useTranslation("jobs");
  return (
    <>
      <Kv label={t("detail.recruiter")}>{carrier.contact_name || "—"}</Kv>
      <Kv label={t("detail.phone")}>{carrier.contact_phone || "—"}</Kv>
      <Kv label={t("detail.email")}>{carrier.contact_email || "—"}</Kv>
      <Kv label={t("detail.method")}>{carrier.submission_method || "—"}</Kv>
      <Kv label={t("detail.replyTime")}>{carrier.reply_time || "—"}</Kv>
      <Kv label={t("detail.notes")}>{carrier.notes || "—"}</Kv>
    </>
  );
}

function CandidatesCard({ job, data, loading, me }: { job: Job; data: JobCandidates | undefined; loading: boolean; me: Me }) {
  const driverActions = useDriverActions();
  const { t } = useTranslation("jobs");
  const shortLabel = (s: ApplicationStatus) => (s === "background_check" ? t("detail.shortBackground") : s === "withdrawn" ? t("carriers:withdrawn") : t(`common:status.${s}`));
  const summary = data
    ? SUMMARY_ORDER.filter((s) => (data.by_status[s] ?? 0) > 0)
        .map((s) => `${shortLabel(s)} ${data.by_status[s]}`)
        .join(" · ")
    : "";
  const mineLabel = me.role === "recruiter" ? t("detail.myCandidates") : t("detail.candidates");
  return (
    <Card>
      <CardHeader title={t("detail.candidatesTitle", { count: job.candidates_count })} subtitle={summary || t("detail.noSubmissions")} />
      <CardBody>
        {loading ? (
          <PageLoading />
        ) : !data || data.results.length === 0 ? (
          <p className="text-[13px] text-muted">{data && data.hidden_count > 0 ? `${t("detail.hiddenOthers", { count: data.hidden_count })}.` : t("detail.noCandidates")}</p>
        ) : (
          <>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] text-ink-soft">
                  <th className="pb-2 text-left font-semibold">{mineLabel}</th>
                  <th className="pb-2 text-left font-semibold">{t("detail.status")}</th>
                  <th className="pb-2 text-left font-semibold">{t("detail.submitted")}</th>
                  <th className="pb-2 text-left font-semibold">{t("detail.carrierReply")}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((a) => (
                  <ContextMenu key={a.id} items={() => driverActions.items(a.driver)}>
                    <tr className="border-t border-line data-[state=open]:bg-primary-soft/50">
                      <td className="py-2">
                        <Link to={`/drivers/${a.driver.id}`} className="font-semibold hover:underline">
                          {a.driver.full_name}
                        </Link>
                        {me.role !== "recruiter" && a.driver.owner && <span className="ml-1 text-[11px] text-muted">· <PersonLink id={a.driver.owner.id} name={a.driver.owner.display_name} /></span>}
                      </td>
                      <td className="py-2">
                        <ApplicationPill status={a.status} reason={a.rejection_reason} />
                      </td>
                      <td className="py-2 text-muted">{formatWhen(a.submitted_at)}</td>
                      <td className="py-2">
                        <CarrierReply status={a.status} submittedAt={a.submitted_at} reply={a.carrier_reply} decidedAt={a.decided_at} startDate={a.start_date} />
                      </td>
                    </tr>
                  </ContextMenu>
                ))}
              </tbody>
            </table>
            {data.hidden_count > 0 && <p className="mt-3 text-[12px] text-muted">+ {t("detail.hiddenOthers", { count: data.hidden_count })}</p>}
          </>
        )}
      </CardBody>
      {driverActions.dialogs}
    </Card>
  );
}

